import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "../components/Header";
import "../styles/UniversityApplicationForm.css";

const CLIENT_IDS: Record<string, string | null> = {
  trinity: "trinity_college",
  ucd: "dublin_college",
  // Unsupported universities
  nuig: null,
  ucc: null,
  dcu: null
};

const UniversityApplicationForm: React.FC = () => {
  const { slug } = useParams();
  const [documentUploaded, setDocumentUploaded] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const requestIdKey = "requestId";
  const returnURLKey = "returnURL";

  const clientId = slug ? CLIENT_IDS[slug] : null;
  const unsupported = clientId === null;

  // Generate a requestId on load
  useEffect(() => {
    let requestId = sessionStorage.getItem(requestIdKey);
    if (!requestId) {
      requestId = crypto.randomUUID();
      sessionStorage.setItem(requestIdKey, requestId);
    }

    // Save this page's return URL
    sessionStorage.setItem(returnURLKey, window.location.href);

    // Check if document exists
    fetch(`/api/get-document?requestId=${requestId}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.exists) setDocumentUploaded(true);
      })
      .catch(() => setDocumentUploaded(false));
  }, []);

  const handleRequesterPortalRedirect = () => {
    if (!clientId) {
      alert("This university is not supported yet in the simulation.");
      return;
    }

    const procedureCode = "R1";
    const requestId = sessionStorage.getItem(requestIdKey);
    const returnURL = encodeURIComponent(
      sessionStorage.getItem(returnURLKey) || window.location.href
    );

    if (!requestId) {
      console.error("Missing requestId");
      return;
    }

    const url =
      `/api/start-requester-handshake` +
      `?procedureCode=${procedureCode}` +
      `&requestId=${requestId}` +
      `&returnURL=${returnURL}`;

    // Automatically choose correct backend:
    // - localhost:5000 when running locally
    // - same domain when deployed
    const backendBase =
      window.location.origin === "http://localhost:3000"
        ? "http://localhost:5000"
        : window.location.origin;

    window.location.href = `${backendBase}${url}`;
  };

  return (
    <div>
      <Header />
      <div className="application-form-container">
        <Link to="/universities" className="back-link">
          ← Back to University Selection
        </Link>

        <h2>University Application Form</h2>

        <p>
          Application for <strong>{slug?.toUpperCase()}</strong>
        </p>

        {unsupported && (
          <div className="unsupported-warning">
            ❌ This university is not available in the simulation yet.
            Only Trinity College Dublin and University College Dublin are supported.
          </div>
        )}

        <form className="application-form">
          {/* Personal Info */}
          <fieldset disabled={unsupported}>
            <legend>Personal Information</legend>
            <label>First Name * <input type="text" required /></label>
            <label>Surname * <input type="text" required /></label>
            <label>Date of Birth * <input type="date" required /></label>
            <label>PPSN * <input type="text" placeholder="1234567A" required /></label>
          </fieldset>

          {/* Address */}
          <fieldset disabled={unsupported}>
            <legend>Address Details</legend>
            <label>Address Line 1 * <input type="text" required /></label>
            <label>Address Line 2 <input type="text" /></label>
            <label>City/Town * <input type="text" required /></label>
            <label>County * <input type="text" required /></label>
            <label>Eircode * <input type="text" required /></label>
            <label>Country * <input type="text" value="Ireland" readOnly /></label>
          </fieldset>

          {/* Document */}
          <fieldset disabled={unsupported}>
            <legend>Required Documentation</legend>

            {documentUploaded ? (
              <>
                <p className="success-text">✅ Document received from Requester Portal</p>

                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
                  <button
                    type="button"
                    className="secondary-btn"
                    onClick={() => setShowModal(true)}
                  >
                    View Document
                  </button>
                </div>

                {/* Modal */}
                {showModal && (
                  <div
                    className="pdf-modal-overlay"
                    onClick={() => setShowModal(false)}
                  >
                    <div
                      className="pdf-modal-content"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        className="pdf-close-btn"
                        onClick={() => setShowModal(false)}
                      >
                        ✕
                      </button>

                      <iframe
                        src={`/api/download-document?requestId=${sessionStorage.getItem(requestIdKey)}#zoom=FitH`}
                        width="100%"
                        height="600px"
                        title="Document Preview"
                      />
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <label>
                  Birth Certificate *
                  <input type="file" accept=".pdf,.jpg,.png" />
                </label>

                {!unsupported && (
                  <button
                    type="button"
                    className="secondary-btn"
                    onClick={handleRequesterPortalRedirect}
                  >
                    Get Document from Requester Portal
                  </button>
                )}
              </>
            )}
          </fieldset>

          <button
            type="submit"
            className="submit-btn"
            disabled={unsupported}
          >
            Submit Application
          </button>
        </form>

        <div className="data-note">
          <h4>Data Processing & Security</h4>
          <p>
            Your application information will be processed securely through the DOTS system.
            All credentials are encrypted and transmitted across EU member states in compliance with GDPR.
          </p>
        </div>
      </div>
    </div>
  );
};

export default UniversityApplicationForm;
